import { useEffect } from "react";
import type { LiveState } from "@cs2/contracts/live";
import type { Session } from "@cs2/contracts/transport";
import type { SnapshotPayload, Slot } from "@cs2/contracts/domain";
import { slots } from "../../../shared/lib/slots.js";

/**
 * Die Live-Kopplung zwischen Captain und Team.
 *
 * Drei Effekte, die man nur zusammen versteht — bis Phase 11 lagen sie
 * zwischen sechs anderen in `QuickviewPage.tsx`:
 *
 * 1. Der Captain **treibt**: jede eigene Taktikauswahl geht sofort an alle.
 * 2. Alle anderen **folgen** vollständig, solange ein Captain verbunden ist —
 *    eigenes Wegbrowsen währenddessen wäre genau das, was die Live-Ansicht
 *    verhindern soll.
 * 3. Kennt das Matching die eigene Position, springt die Ansicht dorthin.
 */
export function useLiveFollow(options: {
  isCaptain: boolean;
  connected: boolean;
  liveState: LiveState | null;
  liveModeActive: boolean;
  session: Session | null;
  payload: SnapshotPayload | null;
  tacticId: string | undefined;
  player: Slot;
  selectTactic: (tacticId: string) => void;
  showTactic: (target: { mapId: string; side: "T" | "CT"; id: string }) => void;
  setPlayer: (slot: Slot) => void;
  notify: (message: string) => void;
}): void {
  const {
    isCaptain,
    connected,
    liveState,
    liveModeActive,
    session,
    payload,
    tacticId,
    player,
    selectTactic,
    showTactic,
    setPlayer,
    notify,
  } = options;

  useEffect(() => {
    if (!isCaptain || !connected || !tacticId) return;
    // `selectTactic` steht bewusst nicht in der Abhängigkeitsliste: es ist bei
    // jedem Rendern eine neue Funktion, und mitzuführen hieße, die Auswahl in
    // einer Schleife erneut zu senden.
    selectTactic(tacticId);
  }, [isCaptain, connected, tacticId]);

  useEffect(() => {
    const target = liveState?.tacticId;
    if (!liveModeActive || !target || !payload) return;
    const found = payload.tactics.find((item) => item.id === target);
    if (!found || found.id === tacticId) return;
    showTactic({ mapId: found.mapId, side: found.side, id: found.id });
  }, [liveModeActive, liveState?.tacticId, payload]);

  useEffect(() => {
    const userId = session?.user?.id;
    const assignments = liveState?.slotAssignments;
    if (!userId || !assignments) return;
    const ownSlot = slots.find((slot) => assignments[slot] === userId);
    if (!ownSlot || ownSlot === player) return;
    setPlayer(ownSlot);
    notify(`DU BIST ${ownSlot}`);
  }, [session?.user?.id, liveState?.slotAssignments]);
}
